/**
 * /use-cases/support — "from customer question to a checked reply".
 * Namespace `ucSupport`. Dutch is the lead language for this page (the demo
 * video is Dutch); EN stays in lockstep as a faithful translation.
 *
 * Claims discipline: replies are prepared, checked and sent only after the
 * checks that apply — we never claim every reply is manually approved
 * (approval is per the customer's own rules: "standaard" / "waar goedkeuring
 * is vereist"), and never that sending is fully automatic.
 */

export const ucSupportEn = {
  meta: {
    title: "Support and email automation with checks",
    description:
      "See how FixControl links customer questions to the right project, prepares a reply and applies checks before anything is sent.",
  },
  hero: {
    eyebrow: "Support and email",
    title: "From customer question to a",
    highlight: "checked reply",
    subtitle:
      "FixControl links a question from a customer of your organization to the right project, uses that project's tickets, conversations and documentation, and has a specialized AI agent prepare a reply.",
    primaryCta: "Book a demo",
    secondaryCta: "Watch the video",
    bullet1: "Replies are prepared, never sent unchecked",
    bullet2: "Tickets stay in your helpdesk",
    bullet3: "Someone on your team decides where approval is required",
  },
  video: {
    eyebrow: "The demo",
    title: "Watch the support flow",
    description:
      "From an incoming customer email to a prepared, checked reply in the existing ticket.",
  },
  how: {
    eyebrow: "Step by step",
    title: "What happens with a customer question",
    description:
      "Plain language, no magic: the same steps you see in the video, in order.",
    steps: {
      s1: { tag: "Question", title: "A customer of your organization asks a question", body: "By email or through your helpdesk — the ticket stays where it is." },
      s2: { tag: "Project", title: "FixControl finds the right project", body: "The mailbox or helpdesk is linked to a project, so the question lands with the right information." },
      s3: { tag: "Research", title: "The support agent investigates the question", body: "It reads earlier tickets, related conversations and the project's documentation." },
      s4: { tag: "Draft", title: "A reply is prepared", body: "A draft reply, based on the project's own information. Nothing has been sent yet." },
      s5: { tag: "Checks", title: "Rules and safety are checked", body: "FixControl checks the rules that apply and screens the draft — for example for sensitive information." },
      s6: { tag: "Decision", title: "Someone on your team decides where needed", body: "By default someone on your own team reviews replies where approval is required by your rules." },
      s7: { tag: "Reply", title: "The reply goes back through the existing channel", body: "The customer receives the reply in the same thread or ticket — no new tool for your customers." },
    },
  },
  stay: {
    eyebrow: "Your tools stay",
    title: "Your team keeps working where it already works",
    body:
      "Tickets stay in your helpdesk. Email stays in your existing mail environment. FixControl uses the linked information, prepares the work and checks what may happen with it.",
  },
  related: {
    engineering: {
      label: "Also see bug and code automation",
      description: "From bug report to a tested change proposal and a pull request after approval.",
    },
    approvedReplies: {
      label: "Approved Freshdesk & Jira replies",
      description: "How prepared replies reach your helpdesk with the checks that apply.",
    },
    product: {
      label: "How FixControl works",
      description: "The full product tour: information, proposals, checks and decisions.",
    },
  },
  faq: {
    eyebrow: "Questions",
    title: "Frequently asked about support automation",
    q1: {
      q: "Does FixControl send replies automatically?",
      a: "FixControl prepares replies and checks them. Where your rules require approval, someone on your team decides before anything reaches your customer. You set those rules yourself, per project.",
    },
    q2: {
      q: "Do tickets stay in our helpdesk?",
      a: "Yes. Tickets stay in your helpdesk (for example Freshdesk, Jira, Odoo or TOPdesk) and email stays in your mail environment. FixControl works with the linked information and writes back only through the existing channels.",
    },
    q3: {
      q: "Which information does FixControl use for a reply?",
      a: "Only the sources you link to the project: earlier tickets, conversations and documentation. Not information from other projects.",
    },
    q4: {
      q: "Does this work with email too?",
      a: "Yes. Gmail and Office 365 can be linked as mail sources; the reply goes back in the same email thread.",
    },
  },
  cta: {
    title: "See the support flow on your own tickets",
    description: "In one demo we walk through a real customer question, from intake to checked reply.",
    primary: "Book a demo",
    secondary: "Contact us",
  },
};

export const ucSupportNl = {
  meta: {
    title: "Support- en e-mailautomatisering met controle",
    description:
      "Bekijk hoe FixControl klantvragen aan het juiste project koppelt, een antwoord voorbereidt en controles toepast voordat het wordt verstuurd.",
  },
  hero: {
    eyebrow: "Support en e-mail",
    title: "Van klantvraag naar",
    highlight: "gecontroleerd antwoord",
    subtitle:
      "FixControl koppelt een vraag van een klant van je organisatie aan het juiste project, gebruikt de bijbehorende tickets, gesprekken en documentatie en laat een gespecialiseerde AI-agent een antwoord voorbereiden.",
    primaryCta: "Plan een demo",
    secondaryCta: "Bekijk de video",
    bullet1: "Antwoorden worden voorbereid, nooit ongecontroleerd verstuurd",
    bullet2: "Tickets blijven in je helpdesk",
    bullet3: "Iemand van je team beslist waar goedkeuring is vereist",
  },
  video: {
    eyebrow: "De demo",
    title: "Bekijk de supportflow",
    description:
      "Van een binnenkomende klantmail naar een voorbereid, gecontroleerd antwoord in het bestaande ticket.",
  },
  how: {
    eyebrow: "Stap voor stap",
    title: "Wat er met een klantvraag gebeurt",
    description: "Gewone taal, geen magie: dezelfde stappen als in de video, op volgorde.",
    steps: {
      s1: { tag: "Vraag", title: "Een klant van je organisatie stelt een vraag", body: "Per e-mail of via je helpdesk — het ticket blijft gewoon staan waar het staat." },
      s2: { tag: "Project", title: "FixControl vindt het juiste project", body: "De mailbox of helpdesk is aan een project gekoppeld, dus de vraag komt bij de juiste informatie terecht." },
      s3: { tag: "Onderzoek", title: "De supportagent onderzoekt de vraag", body: "Die leest eerdere tickets, gerelateerde gesprekken en de documentatie van het project." },
      s4: { tag: "Concept", title: "Een antwoord wordt voorbereid", body: "Een conceptantwoord, gebaseerd op de informatie van het project zelf. Er is nog niets verstuurd." },
      s5: { tag: "Controle", title: "Regels en veiligheid worden gecontroleerd", body: "FixControl controleert welke regels gelden en toetst het concept — bijvoorbeeld op gevoelige informatie." },
      s6: { tag: "Besluit", title: "Iemand van je team beslist waar nodig", body: "Standaard kijkt iemand van je eigen team mee bij antwoorden waar volgens je regels goedkeuring is vereist." },
      s7: { tag: "Antwoord", title: "Het antwoord gaat terug via het bestaande kanaal", body: "De klant krijgt het antwoord in dezelfde thread of hetzelfde ticket — geen nieuw systeem voor je klanten." },
    },
  },
  stay: {
    eyebrow: "Je systemen blijven",
    title: "Je team blijft werken waar het al werkt",
    body:
      "Tickets blijven in je helpdesksysteem. E-mail blijft in je bestaande mailomgeving. FixControl gebruikt de gekoppelde informatie, bereidt het werk voor en controleert wat ermee mag gebeuren.",
  },
  related: {
    engineering: {
      label: "Bekijk ook bug- en codeautomatisering",
      description: "Van bugmelding naar een getest wijzigingsvoorstel en een pull request na goedkeuring.",
    },
    approvedReplies: {
      label: "Goedgekeurde Freshdesk- en Jira-antwoorden",
      description: "Hoe voorbereide antwoorden met de geldende controles in je helpdesk terechtkomen.",
    },
    product: {
      label: "Zo werkt FixControl",
      description: "De volledige producttour: informatie, voorstellen, controles en beslissingen.",
    },
  },
  faq: {
    eyebrow: "Vragen",
    title: "Veelgestelde vragen over support en e-mail",
    q1: {
      q: "Verstuurt FixControl antwoorden automatisch?",
      a: "FixControl bereidt antwoorden voor en controleert ze. Waar je regels goedkeuring vereisen, beslist iemand van je team voordat er iets naar je klant gaat. Die regels stel je zelf in, per project.",
    },
    q2: {
      q: "Blijven tickets in ons helpdesksysteem?",
      a: "Ja. Tickets blijven in je helpdesk (bijvoorbeeld Freshdesk, Jira, Odoo of TOPdesk) en e-mail blijft in je mailomgeving. FixControl werkt met de gekoppelde informatie en schrijft alleen terug via de bestaande kanalen.",
    },
    q3: {
      q: "Welke informatie gebruikt FixControl voor een antwoord?",
      a: "Alleen de bronnen die je aan het project koppelt: eerdere tickets, gesprekken en documentatie. Geen informatie uit andere projecten.",
    },
    q4: {
      q: "Werkt dit ook met e-mail?",
      a: "Ja. Gmail en Office 365 kunnen als mailbron worden gekoppeld; het antwoord gaat terug in dezelfde e-mailthread.",
    },
  },
  cta: {
    title: "Bekijk de supportflow op je eigen tickets",
    description: "In één demo lopen we een echte klantvraag door, van binnenkomst tot gecontroleerd antwoord.",
    primary: "Plan een demo",
    secondary: "Neem contact op",
  },
};
